import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Navigation from "@/components/Navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Users, 
  Instagram, 
  Calendar, 
  Award, 
  Heart, 
  Star,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  TrendingUp,
  Globe,
  Camera,
  Gift,
  BarChart3,
  Palette,
  CreditCard,
  Share2,
  BookOpen,
  DollarSign,
  Target,
  Tag,
  ShoppingBag,
  Shield,
  Zap
} from "lucide-react";
import { Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { influencerApplicationInsertSchema } from "@shared/schema";
import BearFluencerBadge from "@/components/BearFluencerBadge";

// Extend the base schema with frontend validation
const formSchema = influencerApplicationInsertSchema.extend({
  fullName: z.string().min(2, "Full name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  platforms: z.array(z.string()).min(1, "Please select at least one platform"),
  audienceSize: z.number().min(100, "Minimum audience size is 100"),
  contentTypes: z.array(z.string()).min(1, "Please select at least one content type"),
  whySnuggleCrafters: z.string().min(50, "Please provide at least 50 characters explaining why you want to collaborate"),
  socialHandles: z.object({}).passthrough(),
  websiteUrl: z.string().optional(),
  proposedContent: z.string().optional(),
  previousCollaborations: z.string().optional(),
  mediaKit: z.string().optional(),
  availableStartDate: z.string().optional()
});

type FormData = z.infer<typeof formSchema>;

const PLATFORMS = [
  { value: "instagram", label: "Instagram", icon: Instagram },
  { value: "tiktok", label: "TikTok", icon: Camera },
  { value: "youtube", label: "YouTube", icon: TrendingUp },
  { value: "facebook", label: "Facebook", icon: Users },
  { value: "twitter", label: "Twitter", icon: Globe },
  { value: "blog", label: "Blog/Website", icon: Globe },
  { value: "podcast", label: "Podcast", icon: Star },
  { value: "other", label: "Other", icon: Star }
];

const CONTENT_TYPES = [
  "Parenting",
  "Family Lifestyle", 
  "Education",
  "Children's Entertainment",
  "Bedtime Stories",
  "Early Childhood Development",
  "Product Reviews",
  "Family Travel",
  "Homeschooling",
  "Children's Books",
  "Other"
];

const COMPENSATION_OPTIONS = [
  { value: "flexible", label: "Open to Discussion (Recommended)", icon: Users },
  { value: "free_subscription", label: "Free SnuggleCrafters Subscription", icon: Gift },
  { value: "flat_rate", label: "Flat Rate Payment", icon: DollarSign },
  { value: "product_gifting", label: "Product Gifting", icon: ShoppingBag }
];

export default function SnuggleCraftersInfluencerProgram() {
  const { toast } = useToast();
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [selectedContentTypes, setSelectedContentTypes] = useState<string[]>([]);
  const [socialHandles, setSocialHandles] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      platforms: [],
      audienceSize: 0,
      contentTypes: [],
      whySnuggleCrafters: "",
      proposedContent: "",
      previousCollaborations: "",
      websiteUrl: "",
      mediaKit: "",
      preferredCompensation: "flexible",
      availableStartDate: undefined,
      socialHandles: {}
    }
  });

  const submitApplication = useMutation({
    mutationFn: async (data: FormData) => {
      return apiRequest("/api/influencer/apply", "POST", {
        ...data,
        socialHandles,
        platforms: selectedPlatforms,
        contentTypes: selectedContentTypes
      });
    },
    onSuccess: () => {
      toast({
        title: "Application Submitted Successfully!",
        description: "Welcome to the BearFluencer program! We'll review your application and notify you within 5-7 business days."
      });
      setIsSubmitted(true);
    },
    onError: (error: any) => {
      console.error("Submission error:", error);
      toast({
        title: "Submission Failed",
        description: error?.message || error?.details || "There was an error submitting your application. Please try again.",
        variant: "destructive"
      });
    }
  });

  const onSubmit = (data: FormData) => {
    const formattedData = {
      ...data,
      socialHandles,
      platforms: selectedPlatforms,
      contentTypes: selectedContentTypes,
      availableStartDate: data.availableStartDate || undefined
    };
    submitApplication.mutate(formattedData);
  };

  const handlePlatformChange = (platform: string, checked: boolean) => {
    if (checked) {
      setSelectedPlatforms(prev => [...prev, platform]);
    } else {
      setSelectedPlatforms(prev => prev.filter(p => p !== platform));
      // Remove social handle for unselected platform
      setSocialHandles(prev => {
        const { [platform]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSocialHandleChange = (platform: string, handle: string) => {
    setSocialHandles(prev => ({
      ...prev,
      [platform]: handle
    }));
  };

  if (isSubmitted) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card className="text-center">
          <CardHeader>
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Welcome to the BearFluencer Journey!</CardTitle>
            <CardDescription className="text-lg">
              Your application has been received. Here's what happens next:
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-lg">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                Your BearFluencer Timeline
              </h3>
              <ul className="text-left space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <span className="text-purple-600 font-bold mt-0.5">1.</span>
                  <div>
                    <span className="font-medium">Application Review (5-7 days)</span>
                    <p className="text-muted-foreground">Our team will carefully review your application and social media presence.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-purple-600 font-bold mt-0.5">2.</span>
                  <div>
                    <span className="font-medium">BearFluencer Badge Activation</span>
                    <p className="text-muted-foreground">Once approved, you'll receive your exclusive BearFluencer badge and welcome email.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-purple-600 font-bold mt-0.5">3.</span>
                  <div>
                    <span className="font-medium">Dashboard Access</span>
                    <p className="text-muted-foreground">Access your influencer dashboard to create custom promo codes and track performance.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-purple-600 font-bold mt-0.5">4.</span>
                  <div>
                    <span className="font-medium">Stripe Connect Setup</span>
                    <p className="text-muted-foreground">Complete onboarding to enable automatic payouts directly to your bank account.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-purple-600 font-bold mt-0.5">5.</span>
                  <div>
                    <span className="font-medium">Start Creating!</span>
                    <p className="text-muted-foreground">Share your unique promo codes and track your performance with real-time analytics.</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 p-4 rounded-lg">
              <h4 className="font-semibold mb-2">💡 Pro Tip</h4>
              <p className="text-sm text-muted-foreground">
                While you wait for approval, start planning your content strategy! Think about creative ways to showcase 
                SnuggleCrafters' personalized stories to your audience.
              </p>
            </div>

            <div className="flex gap-3 justify-center">
              <Link href="/">
                <Button variant="outline" data-testid="button-back-home">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
              <Link href="/influencer/dashboard">
                <Button data-testid="button-dashboard">
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800">
      <Navigation />
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4 shadow-lg">
            <Sparkles className="w-4 h-4" />
            BearFluencer Program
            <BearFluencerBadge size="small" showTooltip={false} variant="minimal" />
          </div>
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Become a BearFluencer Partner
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-6">
            Join our exclusive BearFluencer program and unlock powerful monetization tools. 
            Create custom promo codes, access premium marketing resources, and grow your brand with SnuggleCrafters.
          </p>
          
          {/* Statistics */}
          <div className="flex justify-center gap-8 mb-8">
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">500+</p>
              <p className="text-sm text-muted-foreground">Active BearFluencers</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">2M+</p>
              <p className="text-sm text-muted-foreground">Families Reached</p>
            </div>
          </div>
        </div>

        {/* BearFluencer Program Overview Card */}
        <Card className="mb-8 border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Award className="w-6 h-6 text-purple-600" />
              The BearFluencer Advantage
            </CardTitle>
            <CardDescription className="text-base">
              Your complete influencer toolkit for success
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Promo Code Creation */}
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                    <Tag className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold">Custom Promo Codes</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Create unlimited personalized promo codes for your audience. 
                  Track usage and conversions in real-time.
                </p>
              </div>

              {/* Flexible Compensation */}
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold">Flexible Compensation</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Compensation is negotiated privately based on your reach, engagement, 
                  and content quality.
                </p>
              </div>

              {/* Stripe Connect */}
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold">Automatic Payouts</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Powered by Stripe Connect. Get paid automatically every month directly 
                  to your bank account.
                </p>
              </div>

              {/* Analytics Dashboard */}
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold">Performance Analytics</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Track clicks, conversions, earnings, and audience engagement through 
                  your dedicated dashboard.
                </p>
              </div>

              {/* BearFluencer Badge */}
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold">BearFluencer Badge</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Get verified status with our exclusive BearFluencer badge, showing 
                  you're a trusted partner.
                </p>
              </div>

              {/* Marketing Resources */}
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                    <Palette className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold">Brand Resources</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Access professional assets, templates, content guides, and exclusive 
                  marketing materials.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sample Promo Code Preview */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          <Card className="border-green-200 dark:border-green-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-green-600" />
                Sample Promo Code
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 p-6 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-2xl font-bold font-mono">BEAR-SARAH</p>
                    <p className="text-sm text-muted-foreground">Your personalized code</p>
                  </div>
                  <Badge className="bg-green-600 text-white">DISCOUNT</Badge>
                </div>
                <div className="space-y-2 text-sm">
                  <p>✓ Valid for new subscriptions</p>
                  <p>✓ Tracks automatically to your account</p>
                  <p>✓ Compensation negotiated privately</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 dark:border-purple-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-purple-600" />
                Quick Start Benefits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span>Instant promo code generation upon approval</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span>No minimum follower requirement</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span>Monthly training webinars included</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span>Dedicated influencer support team</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span>Early access to new features</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Application Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              BearFluencer Application
            </CardTitle>
            <CardDescription>
              Tell us about yourself and why you'd be a great SnuggleCrafters partner. All fields marked with * are required.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Basic Information</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="fullName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Full Name *</FormLabel>
                          <FormControl>
                            <Input 
                              data-testid="input-full-name"
                              placeholder="Your full name" 
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email Address *</FormLabel>
                          <FormControl>
                            <Input 
                              data-testid="input-email"
                              type="email" 
                              placeholder="your.email@example.com" 
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="websiteUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Website/Portfolio URL</FormLabel>
                        <FormControl>
                          <Input 
                            data-testid="input-website-url"
                            placeholder="https://your-website.com" 
                            {...field} 
                          />
                        </FormControl>
                        <FormDescription>
                          Optional: Share your website, blog, or online portfolio
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Separator />

                {/* Platform Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Platform Information</h3>
                  
                  <div>
                    <Label className="text-sm font-medium">Platforms You Use *</Label>
                    <FormDescription className="mb-3">
                      Select all platforms where you create content
                    </FormDescription>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {PLATFORMS.map((platform) => {
                        const Icon = platform.icon;
                        return (
                          <div key={platform.value} className="flex items-center space-x-2">
                            <Checkbox
                              data-testid={`checkbox-platform-${platform.value}`}
                              id={platform.value}
                              checked={selectedPlatforms.includes(platform.value)}
                              onCheckedChange={(checked) => 
                                handlePlatformChange(platform.value, checked as boolean)
                              }
                            />
                            <Label 
                              htmlFor={platform.value} 
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <Icon className="w-4 h-4" />
                              {platform.label}
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                    {selectedPlatforms.length === 0 && form.formState.isSubmitted && (
                      <p className="text-sm text-red-500 mt-1">Please select at least one platform</p>
                    )}
                  </div>

                  {/* Social Media Handles */}
                  {selectedPlatforms.length > 0 && (
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Social Media Handles</Label>
                      <FormDescription>
                        Enter your username/handle for each selected platform
                      </FormDescription>
                      <div className="grid md:grid-cols-2 gap-3">
                        {selectedPlatforms.map((platform) => {
                          const platformInfo = PLATFORMS.find(p => p.value === platform);
                          const Icon = platformInfo?.icon || Star;
                          return (
                            <div key={platform} className="flex items-center gap-2">
                              <Icon className="w-4 h-4 text-muted-foreground" />
                              <Input
                                data-testid={`input-handle-${platform}`}
                                placeholder={`@your${platform}handle`}
                                value={socialHandles[platform] || ""}
                                onChange={(e) => handleSocialHandleChange(platform, e.target.value)}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <FormField
                    control={form.control}
                    name="audienceSize"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Total Audience Size *</FormLabel>
                        <FormControl>
                          <Input 
                            data-testid="input-audience-size"
                            type="number" 
                            placeholder="e.g., 10000" 
                            {...field}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        </FormControl>
                        <FormDescription>
                          Combined followers/subscribers across all platforms
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Separator />

                {/* Content Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Content Focus</h3>
                  
                  <div>
                    <Label className="text-sm font-medium">Content Types *</Label>
                    <FormDescription className="mb-3">
                      Select all content types you create (check all that apply)
                    </FormDescription>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {CONTENT_TYPES.map((contentType) => (
                        <div key={contentType} className="flex items-center space-x-2">
                          <Checkbox
                            data-testid={`checkbox-content-${contentType.toLowerCase().replace(/\s+/g, '-')}`}
                            id={contentType}
                            checked={selectedContentTypes.includes(contentType)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedContentTypes(prev => [...prev, contentType]);
                              } else {
                                setSelectedContentTypes(prev => prev.filter(c => c !== contentType));
                              }
                            }}
                          />
                          <Label htmlFor={contentType} className="cursor-pointer">
                            {contentType}
                          </Label>
                        </div>
                      ))}
                    </div>
                    {selectedContentTypes.length === 0 && form.formState.isSubmitted && (
                      <p className="text-sm text-red-500 mt-1">Please select at least one content type</p>
                    )}
                  </div>

                  <FormField
                    control={form.control}
                    name="whySnuggleCrafters"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Why SnuggleCrafters? *</FormLabel>
                        <FormControl>
                          <Textarea 
                            data-testid="textarea-why-snugglecrafters"
                            placeholder="Tell us why you want to partner with SnuggleCrafters and how you plan to promote our personalized children's stories..."
                            className="min-h-[100px]"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Minimum 50 characters
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="proposedContent"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Content Ideas</FormLabel>
                        <FormControl>
                          <Textarea 
                            data-testid="textarea-proposed-content"
                            placeholder="Share some content ideas you have for promoting SnuggleCrafters..."
                            className="min-h-[80px]"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Optional: Share your creative vision
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Separator />

                {/* Additional Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Additional Information</h3>
                  
                  <FormField
                    control={form.control}
                    name="previousCollaborations"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Previous Brand Collaborations</FormLabel>
                        <FormControl>
                          <Textarea 
                            data-testid="textarea-previous-collaborations"
                            placeholder="List any relevant brand partnerships or collaborations..."
                            className="min-h-[80px]"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Optional: Help us understand your experience
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="mediaKit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Media Kit URL</FormLabel>
                        <FormControl>
                          <Input 
                            data-testid="input-media-kit"
                            placeholder="Link to your media kit (Google Drive, Dropbox, etc.)"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Optional: Share your media kit or rate card
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="preferredCompensation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preferred Compensation Model</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value ?? undefined}>
                          <FormControl>
                            <SelectTrigger data-testid="select-compensation">
                              <SelectValue placeholder="Select compensation preference" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {COMPENSATION_OPTIONS.map((option) => {
                              const Icon = option.icon;
                              return (
                                <SelectItem key={option.value} value={option.value}>
                                  <div className="flex items-center gap-2">
                                    <Icon className="w-4 h-4" />
                                    {option.label}
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Compensation will be discussed privately during onboarding
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="availableStartDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Available Start Date</FormLabel>
                        <FormControl>
                          <Input 
                            data-testid="input-start-date"
                            type="date"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          When can you start creating content?
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Terms Notice */}
                <Alert>
                  <Shield className="h-4 w-4" />
                  <AlertDescription>
                    By submitting this application, you agree to our BearFluencer program terms including:
                    • Following FTC disclosure guidelines
                    • Maintaining brand standards
                    • Stripe Connect for payment processing
                  </AlertDescription>
                </Alert>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <Button 
                    type="submit" 
                    size="lg" 
                    disabled={submitApplication.isPending}
                    data-testid="button-submit-application"
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  >
                    {submitApplication.isPending ? (
                      <>Submitting...</>
                    ) : (
                      <>
                        Submit Application
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}